import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEVICE_STATUS_LABELS,
  DeviceStatus,
  deviceRegistrationSchema,
  type DeviceRegistrationData,
  type DeviceRegistrationInput,
} from '@fleet/shared';
import { telematicsApi } from '../../api/telematics';
import { Modal } from '../../ui/Modal';
import { Field, inputClass } from '../../ui/Field';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';

/**
 * Ein Gerät eintragen.
 *
 * Der Handschlag ist fail-closed: Ohne registrierte IMEI weist der Empfang
 * jedes Gerät ab. Bis hierher gab es dafür nur den Endpunkt und keine
 * Oberfläche — ein Dongle ließ sich also nur über die Datenbank oder über
 * `TELEMETRY_ACCEPT_UNKNOWN_DEVICES` in Betrieb nehmen.
 *
 * Die IMEI lässt sich nachträglich nicht ändern: Sie ist der natürliche
 * Schlüssel, an dem sämtliche Rohdaten hängen. Ein zweiter Aufruf mit derselben
 * Nummer aktualisiert deshalb den bestehenden Eintrag, statt an einem
 * Unique-Constraint zu scheitern.
 */
export function DeviceForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    // Drei Generics: Im Formular dürfen Felder mit Default fehlen, nach der
    // Prüfung sind sie gesetzt.
  } = useForm<DeviceRegistrationInput, unknown, DeviceRegistrationData>({
    resolver: zodResolver(deviceRegistrationSchema),
    defaultValues: { model: 'FMC003', status: DeviceStatus.ACTIVE },
  });

  const save = useMutation({
    mutationFn: (values: DeviceRegistrationData) => telematicsApi.register(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['telematics-devices'] });
      onClose();
    },
  });

  return (
    <Modal
      title="Gerät registrieren"
      description="Erst danach nimmt der Empfang Daten dieser IMEI an."
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          void handleSubmit((values) => save.mutateAsync(values))(event);
        }}
        className="space-y-4"
      >
        <Field
          label="IMEI"
          hint="15 Ziffern, steht auf dem Gehäuse des Dongles."
          error={errors.imei?.message}
        >
          <input {...register('imei')} className={inputClass} inputMode="numeric" autoFocus />
        </Field>

        <Field label="Modell" error={errors.model?.message}>
          <input {...register('model')} className={inputClass} />
        </Field>

        <Field label="Seriennummer" optional error={errors.serialNumber?.message}>
          <input {...register('serialNumber')} className={inputClass} />
        </Field>

        <Field label="Firmware" optional error={errors.firmware?.message}>
          <input {...register('firmware')} className={inputClass} />
        </Field>

        <Field
          label="Status"
          hint="Nur ein aktives Gerät kommt durch den Handschlag."
          error={errors.status?.message}
        >
          <select {...register('status')} className={inputClass}>
            {Object.values(DeviceStatus).map((status) => (
              <option key={status} value={status}>
                {DEVICE_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </Field>

        {save.isError && <ErrorNotice error={save.error} />}

        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            Registrieren
          </Button>
        </div>
      </form>
    </Modal>
  );
}
